import { fontStack } from './fonts.js';
import type { MeasureText } from '@personalizehq/schemas/ir';

// THE ONE CANVAS MEASURER IN THE CODEBASE. The production renderer, the golden-regeneration
// tools, and the spike's CSS-flow harness arm all import THIS function — three copies collapsed
// to one, because a drifted copy would silently move wrap points.
//
// This is the web implementation of the injected platform primitive (SPEC §6.6): Canvas
// TextMetrics + greedy word wrap, with an over-long word BROKEN BY CHARACTER. That last part is
// NORMATIVE: Core Text, Skia and Minikin all char-break by default, while a word-only wrapper —
// and CSS's default `overflow-wrap: normal` — let the word overflow on ONE line. A line-count
// difference is a height difference; height cascades.

export function makeMeasureText(): MeasureText {
  const g = document.createElement('canvas').getContext('2d')!;
  return (text, font, maxWidth) => {
    // THE SAME STACK THE PAINTER USES — see `fontStack`. Measure with one family and paint
    // with another and every wrap point moves.
    g.font = `${font.weight} ${font.size}px ${fontStack(font.family)}`;
    const full = g.measureText(text).width;
    if (!isFinite(maxWidth) || full <= maxWidth) return { w: full, line_count: 1 };

    let lines = 1;
    let cur = '';
    for (const word of text.split(' ')) {
      const next = cur ? `${cur} ${word}` : word;
      if (g.measureText(next).width <= maxWidth) {
        cur = next;
        continue;
      }
      if (cur) {
        lines++; // the word starts a fresh line
        cur = '';
      }
      // Place the word alone, splitting it if it STILL does not fit.
      let chunk = '';
      for (const ch of word) {
        const grown = chunk + ch;
        if (chunk && g.measureText(grown).width > maxWidth) {
          lines++;
          chunk = ch;
        } else {
          chunk = grown;
        }
      }
      cur = chunk;
    }
    return { w: maxWidth, line_count: lines };
  };
}
